import { useEffect, useState } from 'react';
import { getToken, saveMe, me as whoAmI } from './api.js';
import { withReturn, hereNow } from './gate.jsx';
import { TabBar } from './ui.jsx';

/* ══════════════════════════════════════════════════════════════════════════
 * NOTIFICATION SETTINGS — `08i_Notification_Settings.html`
 *
 * ⚠️ THE SHAPE IS NOT INVENTED HERE. It is spelled out by the database, in
 * `users_notification_prefs_shape` (migration 082, rewritten 17 September):
 *
 *     { version: 1,
 *       reservations: ["whatsapp","email","in_app"],
 *       deals:        [...],
 *       zaryna:       [...],
 *       replies:      [...] }
 *
 * Four kinds, three channels, `version: 1` required, and no key outside that
 * list. A screen that sent anything else would be refused by Postgres — which
 * is the right place for that to be refused.
 *
 * ── ⚠️ '{}' AND [] ARE DIFFERENT ANSWERS, AND THAT IS THE WHOLE DESIGN ──────
 *
 * The schema says it plainly: *"An empty array is a real answer and must stay
 * possible. 'Tell me about nothing of this kind' is a choice an Eleater is
 * entitled to make, and it is different from 'never asked'. The `version` key
 * is what tells those apart: `'{}'` is an account that has never opened the
 * screen."*
 *
 * So this screen must be able to save a row with every switch off, and that
 * save must still carry `version: 1`. Treating "all off" as "nothing to save"
 * would make turning everything off the one setting an Eleater cannot express
 * — and the locked screen promises the opposite in its own words: *"Every kind
 * can be turned off completely — that is an answer, not a gap."*
 *
 * ── AND THERE IS NO PUSH, NO SMS, AND NO GOOGLE ─────────────────────────────
 *
 * Three channels, because Eleat has three. The locked screen answers the
 * Google question itself: it is a way of signing in, not a way of being
 * reached. SMS is not here for the reason in `mail.js` and never will be.
 * ══════════════════════════════════════════════════════════════════════════ */

/* verbatim from the locked screen, in its order */
const KINDS = [
  ['reservations', 'Reservations', ''],
  ['deals', 'Deals near you', ''],
  ['zaryna', 'Zaryna’s picks',
    'When Zaryna finds something she thinks is worth your evening.'],
  ['replies', 'Replies to your reviews',
    'When a restaurant answers something you wrote.'],
];

/* ⚠️ THE THREE THE CONSTRAINT ALLOWS, AND ONLY THOSE.
   ⚠️ WHATSAPP IS OFFERED HERE AND CANNOT DELIVER YET — the WhatsApp door is
   not built, and it waits on Meta. It is kept because it is the locked
   screen's own row and RULE 3 says a control Al approved is not mine to
   remove; and because a preference is a statement about the future, not a
   promise about tonight. RAISED WITH AL rather than quietly dropped. */
const FIRST_TIME = {
  reservations: ['email', 'in_app'],
  replies: ['email', 'in_app'],
  deals: ['in_app'],
  zaryna: ['in_app'],
};

const CHANNELS = [
  ['whatsapp', 'WhatsApp'],
  ['email', 'Email'],
  ['in_app', 'In-app'],
];

export default function Notifications() {
  const signedIn = !!getToken();
  const [loading, setLoading] = useState(true);
  const [prefs, setPrefs] = useState({});
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!signedIn) { setLoading(false); return; }
    let live = true;
    whoAmI()
      .then((m) => {
        if (!live) return;
        const p = m.notification_prefs || {};
        /* ⚠️ AN ACCOUNT THAT HAS NEVER ANSWERED ('{}', no version) STARTS ON
           THE SAFE DEFAULTS — Al-Noor, 21 Oct 2026: "I haven't picked any…
           confused". In-app for everything; email only for what is about
           THEIR OWN booking or review. Deals and Zaryna's picks by email are
           marketing, and Canada's anti-spam law does not accept a box ticked
           for somebody — they turn those on themselves. Nothing is saved
           until they press Save. */
        setPrefs(p && p.version ? p : FIRST_TIME);
      })
      .catch((e) => { if (live) setError(e.message); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [signedIn]);

  const on = (kind, ch) => Array.isArray(prefs[kind]) && prefs[kind].includes(ch);

  function toggle(kind, ch) {
    setSaved(false);
    setPrefs((cur) => {
      const list = Array.isArray(cur[kind]) ? cur[kind] : [];
      const next = list.includes(ch) ? list.filter((c) => c !== ch) : [...list, ch];
      return { ...cur, [kind]: next };
    });
  }

  async function save(e) {
    e.preventDefault();
    if (busy) return;
    setBusy(true); setError(null); setSaved(false);
    try {
      /* ⚠️ `version: 1` IS REQUIRED BY THE CONSTRAINT and is what separates
         "answered none" from "never asked". Every kind is written out, even
         the empty ones, so an Eleater who turns everything off has SAID so. */
      const body = { notification_prefs: { version: 1 } };
      for (const [kind] of KINDS) {
        body.notification_prefs[kind] = Array.isArray(prefs[kind]) ? prefs[kind] : [];
      }
      await saveMe(body);
      setSaved(true);
    } catch (err) {
      setError(err.message);
    } finally { setBusy(false); }
  }

  if (!signedIn) {
    return (
      <div className="home">
        <div className="top">
          <a className="back" href="?profile=1" aria-label="Back">‹</a>
          <h1 className="heroline">Notifications</h1>
        </div>
        <main>
          <>
          <p className="mut">
            Sign in to choose how Eleat reaches you.
          </p>
          <div className="guest">
            <a className="gcta" href={withReturn('?welcome=1', hereNow())}>Sign in or join Eleat</a>
          </div>
          </>
        </main>
        <TabBar active="profile" />
      </div>
    );
  }

  return (
    <form className="home nset" onSubmit={save} noValidate>
      <div className="top">
        <a className="back" href="?settings=1" aria-label="Back">‹</a>
        <h1 className="heroline">Notifications</h1>
      </div>

      <main>
        <p className="note">Choose how Eleat reaches you.</p>

        {loading && <p className="mut">Loading…</p>}

        <div className="sect">WHAT WE TELL YOU ABOUT</div>

        {KINDS.map(([kind, label, sub]) => (
          <div key={kind} className="nrow">
            <div className="nrowt">{label}</div>
            {sub && <div className="nrows">{sub}</div>}
            <div className="chipbox">
              {CHANNELS.map(([ch, chLabel]) => (
                <button key={ch} type="button"
                  className={on(kind, ch) ? 'chip on' : 'chip'}
                  aria-pressed={on(kind, ch)}
                  onClick={() => toggle(kind, ch)}>{chLabel}</button>
              ))}
            </div>
          </div>
        ))}

        

        {error && <div className="err" aria-live="polite">{error}</div>}
        {saved && <div className="mut" aria-live="polite">Saved.</div>}
      </main>

      <footer>
        <button className="cta" type="submit" disabled={busy}>Save changes</button>
        {/* ⚠️ "What has already happened" IS A DIFFERENT SCREEN from "how you
            want to be told", and the locked screen keeps them apart. */}
        <p className="mut">
          What has already happened is in{' '}
          <a className="link" href="?notifications=1">your notifications</a>.
        </p>
      </footer>

      <TabBar active="profile" />
    </form>
  );
}
